<template>
  <div
    class="page-container"
    style="display: flex; width: 100%; padding: 15px">
    <transition name="slide-fade">
      <div
        class="configure-btn-container-mobile"
        v-if="!showFullPage"
        style="
          width: 100%;
          flex-shrink: 0;
          height: 100%;
          overflow-y: auto;
          overflow-x: show;
        ">
        <div
          class="configure-btn subtleshadow"
          @click="
            tab = i;
            showFullPage = true;
          "
          :class="
            tab === i ? 'configure-btn-selected' : 'configure-btn-unselected'
          "
          v-for="(btn, i) in allPages">
          <v-icon
            class="mr-3"
            size="x-large">
            {{ btn.icon }}
          </v-icon>
          <div>{{ btn.name }}</div>
        </div>
      </div>
    </transition>
    <div class="pr-3 button-container">
      <div
        class="configure-btn subtleshadow"
        @click="tab = i"
        :class="
          tab === i ? 'configure-btn-selected' : 'configure-btn-unselected'
        "
        v-for="(btn, i) in allPages">
        <v-icon
          class="mr-3"
          size="x-large">
          {{ btn.icon }}
        </v-icon>
        <div class="btn-text">{{ btn.name }}</div>
      </div>
    </div>
    <div class="configure-page subtleshadow">
      <transition-group
        name="slide-fade"
        tag="div">
        <div
          v-for="(page, i) in allPages"
          :key="page.name"
          class="config-page-container"
          :class="page.card ? 'main-content-card' : ''"
          v-show="tab === i">
          <component
            v-if="page.component"
            :is="page.component"
            v-bind="
              (typeof page.props === 'function' ? page.props() : page.props) ||
              {}
            " />
        </div>
      </transition-group>
    </div>
    <v-btn
      v-if="showFullPage"
      prepend-icon="mdi-arrow-left"
      class="mx-3 subtleshadow back-btn"
      flat
      @click="showFullPage = false">
      Back
    </v-btn>
  </div>
</template>
<script setup>
import { deluge } from '../../stores/deluge.js';

// Lazy-load subpages to avoid bundling all Configure subcomponents into a single chunk
import { ref, onMounted, computed, defineAsyncComponent } from 'vue';
import RolesConfig from './RolesConfig.vue';
import Connections from './Connections.vue';

// Async-load configure subpages
const ClientConfig = defineAsyncComponent(() => import('./ClientConfig.vue'));
const YourCanvasPro = defineAsyncComponent(() => import('./YourCanvasPro.vue'));
const FrameConfig = defineAsyncComponent(() => import('./FrameConfig.vue'));

const AssetsConfig = defineAsyncComponent(() => import('./AssetsConfig.vue'));
const TaggingConfig = defineAsyncComponent(() => import('./TaggingConfig.vue'));
const AppsConfig = defineAsyncComponent(() => import('./AppsConfig.vue'));
const UserConfig = defineAsyncComponent(() => import('./UserConfig.vue'));
const DefaultQueueConditionsConfig = defineAsyncComponent(
  () => import('./DefaultQueueConditionsConfig.vue')
);
const VentuzConfig = defineAsyncComponent(() => import('./VentuzConfig.vue'));
const AudioConfig = defineAsyncComponent(() => import('./AudioConfig.vue'));
const HardwareOutputsConfig = defineAsyncComponent(
  () => import('./HardwareOutputsConfig.vue')
);
const DesktopConfig = defineAsyncComponent(
  () => import('./DesktopConfig.vue')
);

const showFullPage = ref(false);
const processed_search = ref(false);
const search = ref('');
const current_user = ref('');
const tab = ref(0);

// source list of available pages (Hardware removed — handled via BottomBar)
const sourceBtnList = [
  { name: 'Client Settings', icon: 'mdi-monitor-cellphone' },
  { name: 'Your Canvas', icon: 'mdi-card-text-outline' },
  { name: 'Connections', icon: 'mdi-lan-connect', oneOnly: true },
  { name: 'Assets', icon: 'mdi-folder-multiple-image' },
  { name: 'Tags', icon: 'mdi-tag-multiple-outline' },
  { name: 'Frames', icon: 'mdi-picture-in-picture-top-right', oneOnly: true },
  { name: 'Hardware Outputs', icon: 'mdi-monitor-multiple' },
  { name: 'Queue Conditions', icon: 'mdi-playlist-play' },
  { name: 'Apps', icon: 'mdi-apps' },
  { name: 'Users', icon: 'mdi-account-group' },
  { name: 'User Roles', icon: 'mdi-badge-account-alert' },
  { name: 'Graphics Engine', icon: 'mdi-monitor-screenshot', oneOnly: true },
  { name: 'Audio', icon: 'mdi-volume-high', oneOnly: true },
  { name: 'Desktop', icon: 'mdi-monitor', oneOnly: true },
];

// map page name -> component and extra metadata
const pageMap = {
  'Client Settings': { component: ClientConfig, card: true },
  'Your Canvas': { component: YourCanvasPro, card: false },
  Connections: { component: Connections, card: false },
  Assets: { component: AssetsConfig, card: false },
  Tags: {
    component: TaggingConfig,
    card: false,
    props: () => ({ tags: deluge.tags }),
  },
  Frames: { component: FrameConfig, card: false },
  'Hardware Outputs': { component: HardwareOutputsConfig, card: true },
  'Queue Conditions': { component: DefaultQueueConditionsConfig, card: true },
  Apps: { component: AppsConfig, card: true },
  Users: { component: UserConfig, card: true },
  'User Roles': { component: RolesConfig, card: true },
  'Graphics Engine': { component: VentuzConfig, card: true },
  Audio: { component: AudioConfig, card: true },
  Desktop: { component: DesktopConfig, card: true },
};

const allPages = computed(() => {
  const productName =
    deluge['canvaspro_core_info']?.['product_name']?.toLowerCase();
  const omni = productName === 'omni';
  const orb = productName === 'orb';

  return sourceBtnList
    .map((btn) => {
      const map = pageMap[btn.name] || {};
      // oneOnly items should be hidden for omni, but shown for one and orb
      let visible = btn.oneOnly ? !omni : true;

      // Hide Hardware Outputs for orb and omni licenses
      if ((orb || omni) && btn.name === 'Hardware Outputs') {
        visible = false;
      }

      return {
        name: btn.name,
        icon: btn.icon,
        component: map.component || null,
        props: map.props || null,
        card: !!map.card,
        visible,
      };
    })
    .filter((page) => page.visible);
});
</script>

<style scoped>
@import './config.css';
</style>
